import React, { useState, useEffect } from 'react';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t } from '../i18n/translations';

const TYPE_LABELS = {
  doctor_visit: '🩺 Doctor Visit',
  blood_test: '🩸 Blood Test',
  dentist: '🦷 Dentist',
  specialist: '👨‍⚕️ Specialist',
  other: '📋 Other'
};

const TYPE_COLORS = {
  doctor_visit: { bg: '#dbeafe', color: '#1d4ed8' },
  blood_test: { bg: '#fee2e2', color: '#dc2626' },
  dentist: { bg: '#fef9c3', color: '#b45309' },
  specialist: { bg: '#f3e8ff', color: '#7c3aed' },
  other: { bg: '#f1f5f9', color: '#475569' }
};

// Map app language codes to BCP-47 locale strings for Intl APIs
const LOCALE_MAP = {
  en: 'en-US', ar: 'ar-SA', fr: 'fr-FR', es: 'es-ES',
  ur: 'ur-PK', zh: 'zh-CN', ja: 'ja-JP', tl: 'tl-PH',
  hi: 'hi-IN', pa: 'pa-IN',
};

const s = {
  page: { maxWidth: 960, margin: '0 auto', padding: '32px 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--heading)' },
  headerRight: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  viewToggle: { display: 'flex', background: 'var(--surface2)', borderRadius: 10, padding: 4, gap: 4 },
  viewBtn: { padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: { padding: '8px 18px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  card: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 14, boxShadow: 'var(--shadow)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  cardLeft: { flex: 1, minWidth: 0 },
  cardTitle: { fontSize: 17, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 },
  cardSub: { color: 'var(--text3)', fontSize: 13, marginBottom: 3 },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, marginRight: 6 },
  cardBtns: { display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
  editBtn: { background: 'var(--surface2)', color: 'var(--text3)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  delBtn: { background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  doneBtn: { background: 'var(--success-bg)', color: 'var(--success-text)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  resultsBtn: { background: 'var(--primary-light)', color: 'var(--primary)', border: 'none', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  resultsBox: { background: 'var(--success-bg)', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginTop: 8, fontSize: 13, color: 'var(--success-text)' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 },
  modalCard: { background: 'var(--surface)', padding: 36, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 20, fontWeight: 700, marginBottom: 22, color: 'var(--heading)' },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--text2)', fontSize: 14 },
  input: { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 14, boxSizing: 'border-box', background: 'var(--surface2)', color: 'var(--text)' },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  cancelBtn: { flex: 1, padding: 12, border: '1.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 600 },
  saveBtn: { flex: 1, padding: 12, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  empty: { textAlign: 'center', padding: 50, color: 'var(--text4)', background: 'var(--surface2)', borderRadius: 12 },
  errorMsg: { color: 'var(--error-text)', fontSize: 14, marginBottom: 12 },
  recurringRow: { background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', marginBottom: 14, border: '1.5px solid var(--border)' },
  recurringCheck: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0 },
  checkLabel: { fontWeight: 600, color: 'var(--text2)', fontSize: 14, cursor: 'pointer' },
  repeatRow: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 },
  repeatInput: { width: 70, padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--surface)', color: 'var(--text)', textAlign: 'center' },
  repeatSelect: { padding: '9px 10px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 14, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' },
  recurBadge: { display: 'inline-block', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, marginLeft: 6 },
  // Calendar
  calNav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  calTitle: { fontSize: 18, fontWeight: 700, color: 'var(--heading)' },
  calNavBtn: { background: 'var(--surface2)', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 16, color: 'var(--text3)' },
  calGrid: { background: 'var(--surface)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow)' },
  calDayHeader: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' },
  calDayName: { textAlign: 'center', padding: '10px 0', fontSize: 12, fontWeight: 700, color: 'var(--text3)' },
  calWeek: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--border)' },
  calCell: { minHeight: 90, padding: 8, borderRight: '1px solid var(--border)', cursor: 'pointer' },
  calDateNum: { fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 },
  calDateNumToday: { background: 'var(--primary)', color: '#fff', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, marginBottom: 4 },
  calDot: { fontSize: 11, padding: '2px 6px', borderRadius: 10, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 600 },
  calSelected: { background: 'var(--surface)', borderRadius: 14, padding: 20, marginTop: 20, boxShadow: 'var(--shadow)' },
  calSelectedTitle: { fontSize: 16, fontWeight: 700, color: 'var(--heading)', marginBottom: 14 }
};

const emptyForm = { title: '', type: 'doctor_visit', personType: 'senior', personId: '', personName: '', date: '', time: '09:00', location: '', notes: '', recurring: false, recurringInterval: 1, recurringUnit: 'weeks' };

export default function Appointments() {
  const { language } = useApp();
  const T = (key) => t(language, key);
  const locale = LOCALE_MAP[language] || 'en-US';

  // Generate localized day and month names using the user's language
  const DAYS = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2021, 0, 3 + i))
  );
  const MONTHS = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2021, i, 1))
  );
  const [appointments, setAppointments] = useState([]);
  const [seniors, setSeniors] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [viewMode, setViewMode] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [showResultsModal, setShowResultsModal] = useState(null);
  const [resultsText, setResultsText] = useState('');
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [apptRes, seniorRes, caregiverRes] = await Promise.all([
      api.get('/appointments'), api.get('/seniors'), api.get('/caregivers')
    ]);
    setAppointments(apptRes.data);
    setSeniors(seniorRes.data);
    setCaregivers(caregiverRes.data);
  };

  const openAdd = () => { setEditTarget(null); setForm(emptyForm); setFormError(''); setShowModal(true); };

  const openEdit = (appt) => {
    setEditTarget(appt);
    setForm({
      title: appt.title, type: appt.type, personType: appt.personType,
      personId: appt.personId || '', personName: appt.personName || '',
      date: appt.date, time: appt.time, location: appt.location || '',
      notes: appt.notes || '', recurring: appt.recurring || false,
      recurringInterval: appt.recurringInterval || 1, recurringUnit: appt.recurringUnit || 'weeks'
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditTarget(null); setForm(emptyForm); setFormError(''); };

  const handlePersonChange = (personType, personId) => {
    let personName = '';
    if (personType === 'senior') { const s2 = seniors.find(x => x._id === personId); personName = s2 ? s2.name : ''; }
    else { const c = caregivers.find(x => x._id === personId); personName = c ? c.name : ''; }
    setForm(f => ({ ...f, personType, personId, personName }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      if (editTarget) await api.put(`/appointments/${editTarget._id}`, form);
      else await api.post('/appointments', form);
      closeModal();
      fetchAll();
    } catch (err) { setFormError(err.response?.data?.error || 'Something went wrong'); }
  };

  const markComplete = async (id) => { await api.put(`/appointments/${id}/complete`); fetchAll(); };
  const deleteAppt = async (id) => { if (!window.confirm('Delete this appointment?')) return; await api.delete(`/appointments/${id}`); fetchAll(); };
  const openResults = (appt) => { setShowResultsModal(appt); setResultsText(appt.results || ''); };
  const saveResults = async () => { await api.put(`/appointments/${showResultsModal._id}/results`, { results: resultsText }); setShowResultsModal(null); fetchAll(); };

  const filtered = appointments.filter(a => {
    if (filter === 'upcoming') return a.status === 'upcoming';
    if (filter === 'completed') return a.status === 'completed';
    if (filter === 'missed') return a.status === 'missed';
    return true;
  });

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date + 'T00:00:00').toLocaleDateString(locale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const statusStyle = (status) => {
    if (status === 'completed') return { bg: '#dcfce7', color: '#16a34a' };
    if (status === 'missed') return { bg: '#fee2e2', color: '#dc2626' };
    return { bg: '#dbeafe', color: '#1d4ed8' };
  };

  const counts = {
    upcoming: appointments.filter(a => a.status === 'upcoming').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    missed: appointments.filter(a => a.status === 'missed').length,
    all: appointments.length
  };

  const personOptions = form.personType === 'senior' ? seniors : caregivers;

  // Calendar helpers
  const getCalendarDays = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const daysInPrev = new Date(calYear, calMonth, 0).getDate();
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) days.push({ day: daysInPrev - i, currentMonth: false });
    for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, currentMonth: true });
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ day: i, currentMonth: false });
    return days;
  };

  const getApptForDay = (day, currentMonth) => {
    if (!currentMonth) return [];
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments.filter(a => a.date === dateStr);
  };

  const isToday = (day, currentMonth) => {
    if (!currentMonth) return false;
    return day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
  };

  const selectedDateStr = selectedDate
    ? `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`
    : null;
  const selectedAppts = selectedDateStr ? appointments.filter(a => a.date === selectedDateStr) : [];

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); setSelectedDate(null); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); setSelectedDate(null); };

  const calDays = getCalendarDays();
  const weeks = [];
  for (let i = 0; i < calDays.length; i += 7) weeks.push(calDays.slice(i, i + 7));

  const AppointmentCard = ({ appt, compact = false }) => {
    const typeStyle = TYPE_COLORS[appt.type] || TYPE_COLORS.other;
    const sSt = statusStyle(appt.status);
    return (
      <div style={{ ...s.card, ...(compact ? { marginBottom: 10 } : {}) }}>
        <div style={s.cardLeft}>
          <div style={s.cardTitle}>
            {appt.title}
            {appt.recurring && <span style={s.recurBadge}>🔁 Recurring</span>}
          </div>
          <div style={{ marginBottom: 6 }}>
            <span style={{ ...s.badge, background: typeStyle.bg, color: typeStyle.color }}>{TYPE_LABELS[appt.type] || appt.type}</span>
            <span style={{ ...s.badge, background: sSt.bg, color: sSt.color }}>{appt.status ? appt.status.charAt(0).toUpperCase() + appt.status.slice(1) : 'Upcoming'}</span>
          </div>
          <div style={s.cardSub}>👤 {appt.personName || 'N/A'} ({appt.personType})</div>
          <div style={s.cardSub}>📅 {formatDate(appt.date)} at {appt.time}</div>
          {appt.location && <div style={s.cardSub}>📍 {appt.location}</div>}
          {appt.notes && <div style={{ ...s.cardSub, fontStyle: 'italic' }}>{appt.notes}</div>}
          {appt.results && <div style={s.resultsBox}>📋 Results: {appt.results}</div>}
        </div>
        <div style={s.cardBtns}>
          {appt.status === 'upcoming' && <button style={s.doneBtn} onClick={() => markComplete(appt._id)}>{T('markComplete')}</button>}
          {appt.status === 'completed' && (
            <button style={s.resultsBtn} onClick={() => openResults(appt)}>
              {appt.results ? 'Edit Results' : T('addResults')}
            </button>
          )}
          <button style={s.editBtn} onClick={() => openEdit(appt)}>{T('edit')}</button>
          <button style={s.delBtn} onClick={() => deleteAppt(appt._id)}>{T('delete')}</button>
        </div>
      </div>
    );
  };

  return (
    <div style={s.page} className="page-wrap">
      <div style={s.header}>
        <div style={s.title} className="big-title">{T('appointments')}</div>
        <div style={s.headerRight}>
          <div style={s.viewToggle}>
            <button style={{ ...s.viewBtn, background: viewMode === 'list' ? 'var(--primary)' : 'transparent', color: viewMode === 'list' ? '#fff' : 'var(--text3)' }}
              onClick={() => setViewMode('list')}>≡ {T('listView')}</button>
            <button style={{ ...s.viewBtn, background: viewMode === 'calendar' ? 'var(--primary)' : 'transparent', color: viewMode === 'calendar' ? '#fff' : 'var(--text3)' }}
              onClick={() => setViewMode('calendar')}>🗓 {T('calendarView')}</button>
          </div>
          <button style={s.btn} onClick={openAdd}>+ {T('addAppointment')}</button>
        </div>
      </div>

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <>
          <div style={s.tabs} className="tab-bar">
            {[['upcoming', T('upcoming')], ['completed', T('completed')], ['missed', 'Missed'], ['all', 'All']].map(([key, label]) => (
              <button key={key} style={{ ...s.tab, background: filter === key ? 'var(--primary)' : 'var(--surface2)', color: filter === key ? '#fff' : 'var(--text3)' }}
                onClick={() => setFilter(key)}>{label} ({counts[key]})</button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>📅</div>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No {filter} appointments</div>
              <div style={{ fontSize: 14 }}>Click "+ {T('addAppointment')}" to schedule one</div>
            </div>
          ) : (
            filtered.map(appt => <AppointmentCard key={appt._id} appt={appt} />)
          )}
        </>
      )}

      {/* CALENDAR VIEW */}
      {viewMode === 'calendar' && (
        <>
          <div style={s.calNav}>
            <button style={s.calNavBtn} onClick={prevMonth} aria-label="Previous month">‹</button>
            <div style={s.calTitle} aria-live="polite">{MONTHS[calMonth]} {calYear}</div>
            <button style={s.calNavBtn} onClick={nextMonth} aria-label="Next month">›</button>
          </div>
          <div style={s.calGrid} className="table-wrap">
            <div style={s.calDayHeader}>
              {DAYS.map(d => <div key={d} style={s.calDayName}>{d}</div>)}
            </div>
            {weeks.map((week, wi) => (
              <div key={wi} style={s.calWeek}>
                {week.map((cell, ci) => {
                  const appts = getApptForDay(cell.day, cell.currentMonth);
                  const todayCell = isToday(cell.day, cell.currentMonth);
                  const selected = selectedDate === cell.day && cell.currentMonth;
                  return (
                    <div key={ci} style={{
                      ...s.calCell,
                      background: selected ? 'var(--primary-light)' : todayCell ? 'var(--primary-light)' : !cell.currentMonth ? 'var(--surface2)' : 'var(--surface)',
                      opacity: !cell.currentMonth ? 0.5 : 1,
                    }}
                      onClick={() => cell.currentMonth && setSelectedDate(selected ? null : cell.day)}>
                      {todayCell
                        ? <div style={s.calDateNumToday}>{cell.day}</div>
                        : <div style={s.calDateNum}>{cell.day}</div>
                      }
                      {appts.slice(0, 2).map((a, ai) => {
                        const tc = TYPE_COLORS[a.type] || TYPE_COLORS.other;
                        return <div key={ai} style={{ ...s.calDot, background: tc.bg, color: tc.color }}>{a.title}</div>;
                      })}
                      {appts.length > 2 && <div style={{ fontSize: 11, color: 'var(--text4)', fontWeight: 600 }}>+{appts.length - 2} more</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {selectedDate && selectedAppts.length > 0 && (
            <div style={s.calSelected}>
              <div style={s.calSelectedTitle}>📅 {formatDate(selectedDateStr)} — {selectedAppts.length} appointment{selectedAppts.length > 1 ? 's' : ''}</div>
              {selectedAppts.map(appt => <AppointmentCard key={appt._id} appt={appt} compact />)}
            </div>
          )}
          {selectedDate && selectedAppts.length === 0 && (
            <div style={{ ...s.empty, marginTop: 20, padding: 24 }}>
              <div style={{ fontSize: 14 }}>No appointments on {formatDate(selectedDateStr)}</div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={s.modal}>
          <div style={s.modalCard} className="modal-inner">
            <div style={s.modalTitle}>{editTarget ? 'Edit Appointment' : T('addAppointment')}</div>
            <form onSubmit={submitForm}>
              {formError && <div style={s.errorMsg}>{formError}</div>}
              <label style={s.label}>{T('appointmentTitle')} *</label>
              <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Annual checkup with Dr. Smith" />
              <label style={s.label}>{T('appointmentType')}</label>
              <select style={s.input} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="doctor_visit">{T('doctor')} Visit</option>
                <option value="blood_test">{T('bloodTest')}</option>
                <option value="dentist">Dentist</option>
                <option value="specialist">Specialist</option>
                <option value="other">{T('other')}</option>
              </select>
              <label style={s.label}>For (Person Type)</label>
              <select style={s.input} value={form.personType} onChange={e => handlePersonChange(e.target.value, '')}>
                <option value="senior">{T('seniors')}</option>
                <option value="caregiver">{T('caregivers')}</option>
              </select>
              <label style={s.label}>Select {form.personType === 'senior' ? T('seniors') : T('caregivers')}</label>
              <select style={s.input} value={form.personId} onChange={e => handlePersonChange(form.personType, e.target.value)}>
                <option value="">-- Select --</option>
                {personOptions.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 12 }} className="form-row">
                <div style={{ flex: 1 }}>
                  <label style={s.label}>{T('appointmentDate')} *</label>
                  <input style={s.input} type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>{T('appointmentTime')} *</label>
                  <input style={s.input} type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} required />
                </div>
              </div>
              <label style={s.label}>Location / Clinic</label>
              <input style={s.input} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. City Hospital, Room 204" />
              <label style={s.label}>{T('notes')}</label>
              <input style={s.input} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Bring blood test results" />

              {/* Recurring */}
              <div style={s.recurringRow}>
                <label style={s.recurringCheck}>
                  <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} />
                  <span style={s.checkLabel}>🔁 {T('recurring')} Appointment</span>
                </label>
                {form.recurring && (
                  <div style={s.repeatRow}>
                    <span style={{ color: 'var(--text3)', fontSize: 14 }}>{T('repeatEvery')}</span>
                    <input style={s.repeatInput} type="number" min={1} max={365} value={form.recurringInterval}
                      onChange={e => setForm({ ...form, recurringInterval: parseInt(e.target.value) || 1 })} />
                    <select style={s.repeatSelect} value={form.recurringUnit} onChange={e => setForm({ ...form, recurringUnit: e.target.value })}>
                      <option value="days">{T('days')}</option>
                      <option value="weeks">{T('weeks')}</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeModal}>{T('cancel')}</button>
                <button type="submit" style={s.saveBtn}>{editTarget ? T('save') : T('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && (
        <div style={s.modal}>
          <div style={{ ...s.modalCard, maxWidth: 420 }} className="modal-inner">
            <div style={s.modalTitle}>{T('results')}</div>
            <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 14 }}>{showResultsModal.title} — {formatDate(showResultsModal.date)}</div>
            <label style={s.label}>{T('results')} / {T('notes')}</label>
            <textarea style={{ ...s.input, minHeight: 100, resize: 'vertical' }}
              value={resultsText} onChange={e => setResultsText(e.target.value)}
              placeholder="e.g. Blood pressure normal, follow-up in 3 months..." />
            <div style={s.row}>
              <button style={s.cancelBtn} onClick={() => setShowResultsModal(null)}>{T('cancel')}</button>
              <button style={{ ...s.saveBtn, background: '#16a34a' }} onClick={saveResults}>{T('save')} Results</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
